import { useCallback } from 'react';
import { ChatMessage, UploadedFile } from '../types';
import { extractS3KeyFromUri } from '../utils/chatHelpers';

interface UseTemplateActionsProps {
    setLocalMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    onPreviewFile?: (file: UploadedFile) => void;
}

/**
 * Hook for template-related actions (view, download)
 */
export const useTemplateActions = ({
    setLocalMessages,
    onPreviewFile
}: UseTemplateActionsProps) => {
    
    // Handle template view
    const handleTemplateView = useCallback(async () => {
        console.log('View template');
        
        // Get template URI from global state
        const templateUri = window.__ADDAPT_TEMPLATE__?.templateUri;
        const templateId = window.__ADDAPT_TEMPLATE__?.templateId;
        
        if (!templateUri) {
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `error-${Date.now()}`,
                type: 'system',
                content: '❌ No template available to view',
                timestamp: new Date(),
                status: 'sent'
            }]);
            return;
        }
        
        try {
            // Show loading message
            const loadingMsgId = `loading-view-${Date.now()}`;
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: loadingMsgId,
                type: 'system',
                content: '⏳ Loading template preview...',
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Download template content using presigned URL
            const { getPresignedDownloadUrl } = await import('../../../../utils/s3Download');
            
            // Extract S3 key from URI
            const s3Key = extractS3KeyFromUri(templateUri);
            const presignedResponse = await getPresignedDownloadUrl(s3Key, `template-${templateId || 'preview'}.json`);
            
            // Fetch the template content
            const response = await fetch(presignedResponse.downloadUrl);
            const templateContent = await response.json();
            
            // Remove loading message
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => msg.id !== loadingMsgId)
            );
            
            // Store template content, mermaid diagram, and AI summary in window for modal
            (window as any).__TEMPLATE_PREVIEW_CONTENT__ = templateContent;
            (window as any).__TEMPLATE_PREVIEW_MERMAID__ = window.__ADDAPT_TEMPLATE__?.mermaidDiagram;
            (window as any).__TEMPLATE_PREVIEW_AI_SUMMARY__ = window.__ADDAPT_TEMPLATE__?.aiSummary;
            
            // Create a file object for preview
            const templateFile = new File(
                [JSON.stringify(templateContent, null, 2)], 
                `template-${templateId || 'preview'}.json`,
                { type: 'application/json' }
            );
            
            // Use the onPreviewFile callback to show in modal
            if (onPreviewFile) {
                const fileToPreview: UploadedFile = {
                    id: `template-${Date.now()}`,
                    name: `template-${templateId || 'preview'}.json`,
                    size: templateFile.size,
                    type: 'application/json',
                    lastModified: Date.now(),
                    originalFile: templateFile
                };
                onPreviewFile(fileToPreview);
                
                // Add success message with specific ID for cleanup
                const successMessageId = 'template-preview-opened';
                setLocalMessages((prev: ChatMessage[]) => {
                    // Remove any existing preview success message first
                    const filtered = prev.filter(msg => msg.id !== successMessageId);
                    return [...filtered, {
                        id: successMessageId,
                        type: 'system',
                        content: '✅ Template preview opened',
                        timestamp: new Date(),
                        status: 'sent'
                    }];
                });
                
                // Store cleanup function globally so modal can call it
                (window as any).__CLEAR_TEMPLATE_PREVIEW_MESSAGE__ = () => {
                    setLocalMessages((prev: ChatMessage[]) => 
                        prev.filter(msg => msg.id !== successMessageId)
                    );
                };
            }
        } catch (error: any) {
            console.error('Failed to view template:', error);
            
            // Remove loading message
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('loading-view-'))
            );
            
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `error-${Date.now()}`,
                type: 'system',
                content: `❌ Failed to load template preview: ${error.message}`,
                timestamp: new Date(),
                status: 'sent'
            }]);
        }
    }, [setLocalMessages, onPreviewFile]);
    
    // Handle template download
    const handleTemplateDownload = useCallback(async () => {
        console.log('Download template');
        
        // Get template URI from global state
        const templateData = window.__ADDAPT_TEMPLATE__;
        const templateUri = templateData?.templateUri || templateData?.templateS3Uri;
        const templateId = templateData?.templateId;
        
        if (!templateUri) {
            const errorMessage: ChatMessage = {
                id: `download-error-${Date.now()}`,
                type: 'system',
                content: '❌ Template not available for download. Please generate a template first.',
                timestamp: new Date()
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, errorMessage]);
            return;
        }
        
        try {
            // Show downloading message
            const downloadingMsgId = `downloading-${Date.now()}`;
            const downloadMessage: ChatMessage = {
                id: downloadingMsgId,
                type: 'system',
                content: '📥 Downloading template...',
                timestamp: new Date()
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, downloadMessage]);
            
            // Get presigned URL
            const { getPresignedDownloadUrl } = await import('../../../../utils/s3Download');
            
            // Extract S3 key from URI
            const s3Key = extractS3KeyFromUri(templateUri);
            const fileName = `template-${templateId || Date.now()}.json`;
            const presignedResponse = await getPresignedDownloadUrl(s3Key, fileName);
            
            // Fetch the file content
            const response = await fetch(presignedResponse.downloadUrl);
            const blob = await response.blob();
            
            // Create blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            
            // Update message to success
            setLocalMessages((prev: ChatMessage[]) => {
                const filtered = prev.filter(msg => msg.id !== downloadingMsgId);
                return [...filtered, {
                    id: `download-success-${Date.now()}`,
                    type: 'system',
                    content: `✅ Template downloaded successfully as ${fileName}`,
                    timestamp: new Date()
                }];
            });
            
        } catch (error: any) {
            console.error('Failed to download template:', error);
            
            // Remove downloading message
            setLocalMessages((prev: ChatMessage[]) => 
                prev.filter(msg => !msg.id.startsWith('downloading-'))
            );
            
            const errorMessage: ChatMessage = {
                id: `download-error-${Date.now()}`,
                type: 'system',
                content: `❌ Failed to download template: ${error.message}`,
                timestamp: new Date()
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, errorMessage]);
        }
    }, [setLocalMessages]);

    return {
        handleTemplateView,
        handleTemplateDownload
    };
};

